// =====================================================
// Service: Push Notification (Frontend)
// File: frontend/src/services/pushService.js
// Đăng ký & quản lý Web Push Notifications
// =====================================================

import api from './api';

/**
 * Kiểm tra trình duyệt có hỗ trợ Push Notification không
 */
export const isPushSupported = () => {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
};

/**
 * Đăng ký Service Worker
 */
export const registerServiceWorker = async () => {
    if (!('serviceWorker' in navigator)) {
        console.warn('Service Worker không được hỗ trợ');
        return null;
    }

    try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        console.log('✅ Service Worker đã đăng ký:', registration.scope);
        return registration;
    } catch (error) {
        console.error('❌ Đăng ký Service Worker thất bại:', error);
        return null;
    }
};

/**
 * Xin quyền thông báo từ user
 * @returns {'granted' | 'denied' | 'default'}
 */
export const requestPermission = async () => {
    if (!('Notification' in window)) return 'denied';

    const permission = await Notification.requestPermission();
    console.log('🔔 Notification permission:', permission);
    return permission;
};

/**
 * Lấy VAPID public key từ server
 */
const getVapidKey = async () => {
    try {
        const res = await api.get('/push/vapid-public-key');
        return res.data?.data;
    } catch (error) {
        console.error('Lấy VAPID key thất bại:', error);
        return null;
    }
};

/**
 * Chuyển VAPID key từ base64 sang Uint8Array (format cho PushManager)
 */
const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; i++) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
};

/**
 * Lấy thông tin thiết bị (để phân biệt các thiết bị)
 */
const getDeviceInfo = () => {
    const ua = navigator.userAgent;
    if (/iPhone|iPad/i.test(ua)) return 'iOS Safari';
    if (/Android/i.test(ua)) return 'Android Chrome';
    if (/Windows/i.test(ua)) return 'Windows Desktop';
    if (/Mac/i.test(ua)) return 'Mac Desktop';
    return 'Unknown';
};

/**
 * Đăng ký push subscription đầy đủ
 * 1. Đăng ký Service Worker
 * 2. Xin quyền notification
 * 3. Subscribe PushManager
 * 4. Gửi subscription lên server
 */
export const subscribePush = async () => {
    if (!isPushSupported()) {
        console.warn('Push Notification không được hỗ trợ trên trình duyệt này');
        return false;
    }

    try {
        // 1. Đăng ký Service Worker
        const registration = await registerServiceWorker();
        if (!registration) return false;

        // 2. Xin quyền
        const permission = await requestPermission();
        if (permission !== 'granted') {
            console.warn('User từ chối thông báo');
            return false;
        }

        // 3. Lấy VAPID key từ server
        const vapidKey = await getVapidKey();
        if (!vapidKey) {
            console.error('Không lấy được VAPID key');
            return false;
        }

        // 4. Kiểm tra subscription cũ
        let subscription = await registration.pushManager.getSubscription();

        // BẪY THƯỜNG GẶP: nếu khóa VAPID của máy chủ từng thay đổi, đăng ký cũ
        // trên máy vẫn còn nhưng gắn với khóa CŨ. Apple/Google sẽ từ chối 403
        // và KHÔNG bao giờ giao thông báo — im lặng, không báo lỗi gì.
        // Nên phải so khóa, khác thì hủy đăng ký cũ và đăng ký lại.
        if (subscription) {
            const cur = subscription.options?.applicationServerKey;
            const want = urlBase64ToUint8Array(vapidKey);
            let same = false;
            if (cur) {
                const a = new Uint8Array(cur);
                same = a.length === want.length && a.every((v, i) => v === want[i]);
            }
            if (!same) {
                console.warn('⚠️ Khóa VAPID đã đổi — hủy đăng ký cũ và đăng ký lại');
                try { await subscription.unsubscribe(); } catch (e) { }
                subscription = null;
            }
        }

        if (!subscription) {
            // 5. Tạo subscription mới
            subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidKey),
            });
            console.log('✅ Đã tạo push subscription mới');
        } else {
            console.log('✅ Đã có push subscription');
        }

        // 6. Gửi lên server
        await api.post('/push/subscribe', {
            subscription: subscription.toJSON(),
            deviceInfo: getDeviceInfo(),
        });

        console.log('✅ Push Notification đăng ký thành công!');
        return true;
    } catch (error) {
        console.error('❌ Đăng ký push thất bại:', error);
        return false;
    }
};

/**
 * Hủy đăng ký push notification
 */
export const unsubscribePush = async () => {
    try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();

        if (subscription) {
            // Hủy trên browser
            await subscription.unsubscribe();

            // Hủy trên server
            await api.post('/push/unsubscribe', {
                endpoint: subscription.endpoint,
            });

            console.log('✅ Đã hủy push notification');
        }
        return true;
    } catch (error) {
        console.error('Hủy push thất bại:', error);
        return false;
    }
};

/**
 * Kiểm tra trạng thái đăng ký hiện tại
 */
export const getPushStatus = async () => {
    if (!isPushSupported()) return 'unsupported';

    const permission = Notification.permission;
    if (permission === 'denied') return 'denied';
    if (permission === 'default') return 'prompt';

    try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        return subscription ? 'subscribed' : 'unsubscribed';
    } catch {
        return 'error';
    }
};


/**
 * Đọc trạng thái thông báo NGAY TRÊN MÁY người dùng.
 * Dùng cho trang Kiểm tra thông báo — biết chính xác đang tắc ở đâu.
 */
export const diagnosePush = async () => {
    const ua = navigator.userAgent;
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const standalone = window.matchMedia('(display-mode: standalone)').matches
        || window.navigator.standalone === true;

    const out = {
        device: getDeviceInfo(),
        is_ios: isIOS,
        installed_pwa: standalone,
        supported: isPushSupported(),
        permission: ('Notification' in window) ? Notification.permission : 'khong-ho-tro',
        sw_registered: false,
        has_subscription: false,
        endpoint: null,
        https: window.location.protocol === 'https:' || window.location.hostname === 'localhost'
    };

    try {
        const regs = await navigator.serviceWorker.getRegistrations();
        out.sw_registered = regs.length > 0;
        const reg = await navigator.serviceWorker.getRegistration('/sw.js')
            || (regs.length ? regs[0] : null);
        if (reg) {
            const sub = await reg.pushManager.getSubscription();
            if (sub) {
                out.has_subscription = true;
                out.endpoint = sub.endpoint.slice(0, 60) + '...';
            }
        }
    } catch (e) {
        out.error = e.message;
    }
    return out;
};

/**
 * Hủy đăng ký cũ rồi đăng ký lại từ đầu (nút "Đăng ký lại")
 */
export const resubscribePush = async () => {
    try {
        const regs = await navigator.serviceWorker.getRegistrations();
        for (const reg of regs) {
            const sub = await reg.pushManager.getSubscription();
            if (sub) {
                try { await api.post('/push/unsubscribe', { endpoint: sub.endpoint }); } catch (e) { }
                try { await sub.unsubscribe(); } catch (e) { }
            }
        }
    } catch (e) { /* bỏ qua, đăng ký lại luôn */ }
    return await subscribePush();
};
